import { RemovedRide, RidesMeta } from '../../rides/types';
import { ReassignOrganizerResponse, RideSeriesResponse, StandaloneRideResponse } from '../types/RidesTypes';
import { getRequest, getWithParamsRequest, patchRequest, postRequest, putRequest } from '../utils/provider';
import { ApiResponse } from '../utils/responseTransform';

export async function getRides(
  queryParams: QueryParams,
  signal?: AbortSignal
): Promise<ApiResponse<{ trips: Ride[]; meta?: RidesMeta }>> {
  return getWithParamsRequest('trips', queryParams, signal);
}

export async function getCancelReasons(signal?: AbortSignal): Promise<ApiResponse<{ reasons: CancelReason[] }>> {
  return getRequest('trips/reasons', signal);
}

export async function bulkCancelRides(payload): Promise<ApiResponse<{ message: string }>> {
  return postRequest('trips/bulk_cancel', payload);
}
export async function bulkCancelRidesByDates(payload): Promise<ApiResponse<{ message: string }>> {
  return postRequest('trips/bulk_cancel_by_date', payload);
}

export async function cancelRides(id: string, payload: CancelRideFormType): Promise<ApiResponse<{ message: string }>> {
  return postRequest(`trips/${id}/cancel`, payload);
}

export async function getTags(
  queryParams: { q?: string; page?: number; perPage?: number },
  signal?: AbortSignal
): Promise<ApiResponse<{ tags: string[]; meta?: { page?: number; perPage?: number; totalCount?: number } }>> {
  return getWithParamsRequest('tags', queryParams, signal);
}

export async function addStandaloneRide(payload: RideFormType): Promise<ApiResponse<StandaloneRideResponse>> {
  return postRequest('trips/standalone', payload);
}

export async function addRideSeries(payload: RideFormType): Promise<ApiResponse<RideSeriesResponse>> {
  return postRequest('trips/series', payload);
}

export async function getRideDetails(slug: string): Promise<ApiResponse<{ trip: RideDetails }>> {
  return getRequest(`trips/${slug}`);
}

export async function getFareEstimate(
  payload: FareEstimateForNewRide | { tripSlug: string }
): Promise<ApiResponse<FareEstimate>> {
  return postRequest('fare_estimate', payload);
}

export async function getCareDriverPhoneNumber(tripId?: number): Promise<ApiResponse> {
  return postRequest('driver_voice_numbers', { trip_id: tripId });
}

export async function updateRide(
  slug: string,
  payload: RideFormType
): Promise<ApiResponse<{ message: string; slug: string; templateSlug?: string }>> {
  return putRequest(`trips/${slug}`, payload);
}

export async function updatePartialStandaloneRide(
  slug: string,
  payload: Partial<RideFormType>
): Promise<ApiResponse<StandaloneRideResponse>> {
  return patchRequest(`trips/${slug}/standalone`, payload);
}

export async function updatePartialRideSeries(
  slug: string,
  payload: Partial<RideFormType>
): Promise<ApiResponse<RideSeriesResponse>> {
  return patchRequest(`trips/${slug}/series`, payload);
}

export async function removeRiders(slug: string, payload): Promise<ApiResponse<{ message: string }>> {
  return postRequest(`trips/${slug}/remove_riders`, payload);
}

export async function findSuggestions(payload: RideFormType): Promise<
  ApiResponse<{
    suggestions: Suggestion[];
  }>
> {
  return postRequest('suggestions', payload);
}

export async function optimizeWaypointsWithSuggestion(payload): Promise<
  ApiResponse<{
    tripSlug: string;
    timeAnchor: string;
    overviewPolyline: string;
    waypoints: RideDetailsWaypoint[];
    legs: SuggestedLeg[];
  }>
> {
  return postRequest('suggestions/optimized', payload);
}

export async function validateWaypoints(
  payload,
  signal?: AbortSignal
): Promise<
  ApiResponse<{
    waypoints: RideDetailsWaypoint[];
  }>
> {
  return postRequest('waypoints/validate', payload, signal, undefined, { localErrorHandling: true });
}

export async function getRemovedRidersRides(
  params: QueryParams,
  signal?: AbortSignal
): Promise<ApiResponse<{ trips: RemovedRide[] }>> {
  return getWithParamsRequest('trips/rider_removals', params, signal);
}

/** @public - consumed in RIQ-304 (ReassignOrganizerModal) */
export async function reassignOrganizer(
  tripSlug: string,
  organizerSlug: string,
  signal?: AbortSignal
): Promise<ApiResponse<ReassignOrganizerResponse>> {
  return patchRequest(`trips/${tripSlug}/reassign_organizer`, { organizerSlug }, signal);
}

export async function getTodaysRides(
  params: { slugs: string[] },
  signal?: AbortSignal
): Promise<
  ApiResponse<{
    trips: Pick<Ride, 'id' | 'slug' | 'startsAt' | 'endsAt' | 'pickupLocation' | 'dropoffLocation'> &
      { fullName: string }[];
  }>
> {
  return getWithParamsRequest('trips/today', params, signal);
}
